import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Film, Camera, Radio, Play, ChevronDown, ShoppingBag, Calendar } from "lucide-react";
import WarpField from "@/components/WarpField";
import { ChromeBar, SilverOrb } from "@/components/Chrome";
import { Telemetry, Radar, Reticle } from "@/components/Telemetry";
import { useConfig } from "@/context/ConfigContext";
import api, { fileUrl, API } from "@/lib/api";

const thumbUrl = (id) => `${API}/media/${id}/thumbnail`;
const HERO_BG = `${API}/hero-bg.png`;

const SERVICES = [
  { code: "01", title: "Cinematic Reels", desc: "Fight films, walk-outs, training cuts. 35mm grit, modern motion." },
  { code: "02", title: "Stills & Editorial", desc: "Noir portraits, gym ambiance, product editorial. Composed frames." },
  { code: "03", title: "Brand Films", desc: "Documentary-grade stories for athletes, brands, and the culture." },
  { code: "04", title: "Live Coverage", desc: "Tournament & event capture. Multi-cam. Post-edited overnight." },
];

const TELEMETRY = [
  { code: "T.01", value: "∞",     label: "Reels Cut" },
  { code: "T.02", value: "9.6",   label: "Mach Edit Speed" },
  { code: "T.03", value: "24/7",  label: "Studio Watch" },
  { code: "T.04", value: "1.0",   label: "Family / In-House" },
];

export default function Home() {
  const navigate = useNavigate();
  const cfg = useConfig();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    if (!sessionStorage.getItem("jitsuent_warp_seen")) {
      sessionStorage.setItem("jitsuent_warp_seen", "1");
      navigate("/warp", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/media", { params: { kind: "video" } })
      .then((r) => setFeatured(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-black text-white" data-testid="home-page">
      {/* HERO — gun-barrel style aerospace mission briefing */}
      <section className="relative min-h-[100svh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})`, opacity: 0.55, filter: "grayscale(100%) contrast(1.1)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black" />
        <div className="absolute inset-0 opacity-60">
          <WarpField density={180} speed={0.5} />
        </div>
        <div className="absolute inset-0 vignette" />

        {/* Mission briefing top strip */}
        <div className="absolute top-28 left-0 right-0 z-10 px-6 lg:px-12 hidden md:block">
          <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-4 font-mono text-[10px] tracking-[0.3em] text-neutral-500">
            <div className="col-span-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-white rounded-full flicker" />
              MISSION.BRIEF // 001
            </div>
            <div className="col-span-3">PAYLOAD: CINEMATIC CAPTURE</div>
            <div className="col-span-3">VECTOR: HYPERSPACE / NOIR</div>
            <div className="col-span-3 text-right">CLEARANCE: TS-FAMILY</div>
          </div>
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-44 pb-24">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-8 flex items-center gap-3">
                  <span className="w-8 h-px bg-white/60" />
                  CREATIVE FIGHT ENTERTAINMENT
                  <span className="w-8 h-px bg-white/60" />
                </div>
                <h1 className="font-bodoni text-[clamp(4rem,12vw,12rem)] leading-[0.82] tracking-tighter font-black">
                  THE
                  <br />
                  <span className="italic">good times</span>
                  <br />
                  <span className="chrome-text">ROLL FOREVER</span>
                </h1>
                <p className="mt-10 max-w-xl font-mono text-sm text-neutral-400 leading-relaxed">
                  JITSUENT — the in-house creative film & photo arm of Joints & Jitsu.
                  We capture the art and science of jiu-jitsu at cinematic speed.
                  Mission control for the culture.
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Link to="/portfolio" data-testid="hero-cta-reels" className="btn-noir">
                    <span>Enter The Reels</span> <ArrowUpRight size={14} />
                  </Link>
                  <Link to="/book" data-testid="hero-cta-book" className="btn-ghost">
                    <span><Calendar size={13} className="inline -mt-0.5 mr-1" /> Book a Session</span>
                  </Link>
                </div>
              </motion.div>
            </div>

            <div className="hidden lg:flex col-span-4 flex-col items-end gap-6">
              <SilverOrb size={260} />
              <Radar size={140} />
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 font-mono text-[10px] tracking-[0.4em] text-neutral-600"
          >
            <span>SCROLL · DESCEND</span>
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <ChevronDown size={14} />
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom marquee */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-neutral-900 bg-black overflow-hidden">
          <div className="marquee-track py-4 font-bodoni text-3xl md:text-5xl text-neutral-800 whitespace-nowrap">
            {Array.from({ length: 4 }).map((_, i) => (
              <span key={i} className="px-8 flex items-center gap-8">
                JITSUENT <span className="text-neutral-700">·</span> 4EVER ROLLIN'
                <span className="text-neutral-700">·</span> ART <span className="text-neutral-700">·</span>
                SCIENCE <span className="text-neutral-700">·</span> JIU-JITSU
                <span className="text-neutral-700">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TELEMETRY */}
      <section className="border-t border-neutral-900 py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-12 flex items-center gap-4">
            <span className="w-12 h-px bg-neutral-700" />
            FLIGHT TELEMETRY / 02
          </div>
          <Telemetry items={TELEMETRY} />
        </div>
      </section>

      {/* MANIFESTO STRIP */}
      <section className="border-t border-neutral-900 py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-4">03 / MANIFESTO</div>
            <h2 className="font-bodoni text-5xl md:text-7xl leading-[0.92]">
              Captured at <span className="italic">ludicrous</span> <br /> speed.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="font-mono text-sm md:text-base text-neutral-300 leading-relaxed">
              From the chaos of competition to the balance and flow of training,
              we document the art and science of motion. Black and white film,
              chrome reflections, the silver streak of a body moving through hyperspace.
            </p>
            <p className="font-mono text-sm md:text-base text-neutral-400 leading-relaxed mt-6">
              Every project is shot, cut, and finished in-house — by the family, for the family.
              No outsourced eyes. No mocks. No filler.
            </p>
            <div className="mt-10 flex items-center gap-6">
              <span className="font-bodoni text-5xl">柔術</span>
              <div className="font-mono text-[10px] text-neutral-500 tracking-[0.3em] leading-relaxed">
                JOINT OSS<br />ROLL READY<br />SYSTEM RESET
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEPARTMENTS */}
      <section className="border-t border-neutral-900 py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-2">04 / DEPARTMENTS</div>
              <h2 className="font-bodoni text-5xl md:text-7xl">What we capture.</h2>
            </div>
            <div className="font-mono text-[10px] text-neutral-600 tracking-[0.3em]">04 DIVISIONS · ALL ACTIVE</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-900">
            {SERVICES.map((s, i) => {
              const Icon = [Film, Camera, Play, Radio][i];
              return (
                <motion.div
                  key={s.code}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-black p-8 lg:p-10 group hover:bg-neutral-950 transition-colors min-h-[300px] flex flex-col justify-between relative overflow-hidden"
                  data-testid={`service-${s.code}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-[10px] text-neutral-600 tracking-[0.3em]">{s.code}</span>
                    <Icon size={16} className="text-neutral-700 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bodoni text-3xl mb-3 leading-tight">{s.title}</h3>
                    <p className="font-mono text-xs text-neutral-500 leading-relaxed">{s.desc}</p>
                  </div>
                  <div className="absolute -bottom-px left-0 h-px bg-white w-0 group-hover:w-full transition-all duration-700" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED REEL */}
      <section className="border-t border-neutral-900 py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-2">05 / FEATURED REELS</div>
              <h2 className="font-bodoni text-5xl md:text-7xl">In the rotation.</h2>
            </div>
            <Link to="/portfolio" className="hidden md:inline-flex link-underline font-mono text-xs tracking-[0.25em]">
              ALL REELS →
            </Link>
          </div>

          {featured.length === 0 ? (
            <div className="border border-neutral-900 p-16 text-center relative overflow-hidden" data-testid="empty-reels">
              <div className="absolute inset-0 flex items-center justify-center opacity-10">
                <Reticle size={240} />
              </div>
              <div className="relative">
                <div className="font-mono text-[10px] text-neutral-600 tracking-[0.3em] mb-3 cursor-blink">
                  AWAITING TRANSMISSION
                </div>
                <p className="font-bodoni italic text-2xl text-neutral-500">
                  The crew is uploading the first cuts.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4">
              {featured.map((m, i) => (
                <Link
                  key={m.id}
                  to="/portfolio"
                  className={`relative group overflow-hidden bg-neutral-950 border border-neutral-900 hover:border-white transition-colors ${
                    i === 0 ? "col-span-12 md:col-span-8 aspect-video" : "col-span-12 md:col-span-4 aspect-video md:aspect-auto"
                  }`}
                  data-testid={`featured-${i}`}
                >
                  <video
                    src={fileUrl(m.id)}
                    poster={m.thumbnail_path ? thumbUrl(m.id) : undefined}
                    preload="metadata"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                    muted
                    loop
                    playsInline
                    onMouseEnter={(e) => e.currentTarget.play()}
                    onMouseLeave={(e) => e.currentTarget.pause()}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 right-4 flex justify-between font-mono text-[10px] tracking-[0.3em] text-neutral-300">
                    <span>REEL.{String(i + 1).padStart(3, "0")}</span>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">▶ PLAY</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="font-bodoni text-2xl">{m.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-neutral-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <WarpField density={120} speed={0.4} />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 py-32 text-center">
          <ChromeBar className="w-32 mx-auto mb-8" />
          <div className="font-mono text-[10px] text-neutral-500 tracking-[0.4em] mb-6">FINAL TRANSMISSION</div>
          <h2 className="font-bodoni text-5xl md:text-8xl leading-none font-black">
            Welcome to <span className="italic">the Family</span>.
          </h2>
          <p className="mt-8 font-mono text-sm text-neutral-400 max-w-xl mx-auto">
            Athletes, artists, creators, coaches — if you've got a story, we've got the lens.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/onboarding" data-testid="cta-home-onboard" className="btn-noir">
              <span>Begin Transmission →</span>
            </Link>
            <Link to="/book" data-testid="cta-home-book" className="btn-ghost">
              <span><Calendar size={13} className="inline -mt-0.5 mr-1" /> Book a Session</span>
            </Link>
            <a href={cfg.shop_url || "https://jointsnjitsu.com"} target="_blank" rel="noreferrer" data-testid="cta-home-shop" className="btn-ghost">
              <span><ShoppingBag size={13} className="inline -mt-0.5 mr-1" /> Shop @ Joints & Jitsu</span>
            </a>
          </div>
        </div>
      </section>

      {/* JOINTS & JITSU CROSS-PROMO STRIP */}
      <section className="border-t border-neutral-900 bg-black relative overflow-hidden" data-testid="family-strip">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-4">PARENT COMPANY</div>
            <div className="font-bodoni text-5xl md:text-7xl leading-[0.92] mb-4">
              <span className="chrome-text">JOINTS & JITSU</span>
            </div>
            <p className="font-mono text-sm text-neutral-400 max-w-xl">
              Apparel, gear, and the original art-science-jiu-jitsu collective.
              JITSUENT is its in-house lens.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
            <a href={cfg.shop_url || "https://jointsnjitsu.com"} target="_blank" rel="noreferrer" className="btn-noir">
              <span><ShoppingBag size={14} className="inline -mt-0.5 mr-1" /> Shop the Drop</span>
            </a>
            <a href={cfg.social?.youtube || "#"} target="_blank" rel="noreferrer" className="btn-ghost">
              <span>YouTube →</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
